import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { CheckCircle2, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";

interface SignupSuccessPageProps {
  onNavigate: (path: string) => void;
}

export default function SignupSuccessPage({ onNavigate }: SignupSuccessPageProps) {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [countdown, setCountdown] = useState(8);

  useEffect(() => {
    const isNewRegistration = sessionStorage.getItem("registration_success") === "true";
    sessionStorage.removeItem("registration_success");

    if (!isNewRegistration) {
      setIsValid(false);
      onNavigate("/dashboard");
    } else {
      setIsValid(true);
    }
  }, [onNavigate]);

  useEffect(() => {
    if (isValid === null || isValid === false) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          const target = sessionStorage.getItem("auth_redirect_target");
          if (target && (target.startsWith("/business/") || target.includes("sharedBusinessId="))) {
            sessionStorage.removeItem("auth_redirect_target");
            onNavigate(target);
          } else {
            onNavigate("/dashboard");
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isValid, onNavigate]);

  if (isValid === null || isValid === false) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] text-slate-800 dark:text-slate-100 flex flex-col justify-center items-center p-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Verifying session...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0F172A] text-slate-800 dark:text-slate-100 flex flex-col justify-center items-center p-6 md:p-12 font-sans relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-xl w-full bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-slate-700/60 rounded-2xl p-10 shadow-xl text-center relative z-10 space-y-8 text-slate-900 dark:text-white"
      >
        <div className="flex justify-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
            className="p-4 bg-emerald-500/10 text-emerald-400 rounded-full border border-emerald-500/20 shadow-lg shadow-emerald-500/5"
          >
            <CheckCircle2 className="w-16 h-16" />
          </motion.div>
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight sm:text-4xl">
            Welcome to BizNearYou!
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm max-w-md mx-auto">
            Your account has been created successfully. We are preparing your workspace dashboard now.
          </p>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-xl p-5 max-w-md mx-auto flex items-start gap-3.5 text-left">
          <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
              Google Ads Conversion Tracking Active
            </h4>
            <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
              New account registration has been verified and registered. Conversion tracking code has processed your sign-up event successfully.
            </p>
          </div>
        </div>

        <div className="space-y-4 pt-4 max-w-md mx-auto">
          <button
            onClick={() => onNavigate("/dashboard")}
            className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold py-3.5 px-6 rounded-xl text-sm transition-all duration-200 tracking-wider flex items-center justify-center gap-2 cursor-pointer shadow-md active:scale-[0.98]"
          >
            Go to Workspace Dashboard <ArrowRight className="w-4 h-4" />
          </button>

          <p className="text-slate-500 text-xs">
            Redirecting automatically in <span className="font-bold text-slate-700 dark:text-slate-400 font-mono">{countdown}s</span>...
          </p>
        </div>
      </motion.div>
    </div>
  );
}
